import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Clock, CheckCircle, ArrowRight, Users, FileText, Zap } from 'lucide-react';
import { MainNavigation } from '@/components/layout/MainNavigation';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export const HowItWorks = () => {
  const steps = [
    {
      number: 1,
      icon: <FileText className="h-8 w-8" />,
      title: "Complete One Application",
      description: "Fill out our comprehensive application form in just 10 minutes. Our AI captures all your business requirements, from industry specifics to employee count and benefit needs. No more filling out multiple forms for different PEOs.",
      time: "10 minutes",
      traditional: "2-3 hours per PEO"
    },
    {
      number: 2,
      icon: <Zap className="h-8 w-8" />,
      title: "AI Matches You from 523 PEOs",
      description: "Our patent-pending technology analyzes your requirements against our complete network of 523 vetted PEOs, considering 200+ variables to find your perfect matches. Traditional methods only evaluate 5 PEOs (less than 1% of the market) based on marketing, not fit.",
      time: "Under 60 seconds",
      traditional: "2-4 weeks"
    },
    {
      number: 3,
      icon: <Users className="h-8 w-8" />,
      title: "Receive Complete Quotes",
      description: "Get qualified proposals from 3-5 pre-screened PEO providers within 1 week. All quotes are competitive, transparent, and tailored to your specific business needs. PEO quotes require proper evaluation time for accuracy.",
      time: "1 week",
      traditional: "6-8 weeks"
    }
  ];

  const benefits = [
    "Save 83% of your time compared to traditional PEO shopping",
    "Access all 523 PEOs instead of just 5 random providers",
    "Matches based on fit, not marketing or networking",
    "All PEOs are pre-vetted through our rigorous certification process",
    "Competitive quotes with transparent pricing",
    "Expert guidance throughout the selection process",
    "Zero bias - AI-driven recommendations only"
  ];

  return (
    <>
      <Helmet>
        <title>How The PEO App Works - 83% Faster PEO Matching | The PEO App</title>
        <meta name="description" content="Discover how The PEO App revolutionizes PEO shopping with our 3-step process that saves 83% of your time. Access all 523 PEOs with one application instead of just 5 random providers." />
        <meta name="keywords" content="how PEO works, PEO selection process, fast PEO quotes, PEO shopping made easy, AI PEO matching" />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <MainNavigation />
        <main className="pt-16">
          {/* Hero Section */}
          <section className="py-24 bg-gradient-to-br from-primary/5 to-secondary/5">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center max-w-4xl mx-auto"
              >
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                  <Clock className="h-4 w-4" />
                  83% Faster Process
                </div>
                <h1 className="text-4xl md:text-6xl font-bold mb-6">
                  How The PEO App
                  <span className="text-primary block">Saves Your Time</span>
                </h1>
                <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                  Our revolutionary 3-step process streamlines PEO shopping from months to weeks. 
                  Access all 523 PEOs with one application instead of evaluating just 5 random providers.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button asChild size="lg">
                    <Link to="/signup">
                      Start Your Search
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/peo-vetting-process">View Our Vetting Process</Link>
                  </Button>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Time Comparison */}
          <section className="py-16 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="text-center p-6 bg-background rounded-xl border"
                >
                  <h3 className="text-2xl font-bold text-destructive mb-4">Traditional Method</h3>
                  <div className="text-3xl font-bold text-destructive mb-2">6-8 weeks</div>
                  <p className="text-muted-foreground">Multiple applications, endless calls, evaluating just 5 random PEOs</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="text-center p-6 bg-primary text-white rounded-xl"
                >
                  <h3 className="text-2xl font-bold mb-4">The PEO App</h3>
                  <div className="text-3xl font-bold mb-2">1 Week</div>
                  <p className="opacity-90">One application, complete market evaluation, quality matches</p>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Process Steps */}
          <section className="py-24">
            <div className="container mx-auto px-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center mb-16"
              >
                <h2 className="text-3xl font-bold mb-4">Our Simple 3-Step Process</h2>
                <p className="text-muted-foreground max-w-3xl mx-auto">
                  Experience the fastest, most efficient way to find your perfect PEO partner.
                </p>
              </motion.div>

              <div className="max-w-6xl mx-auto space-y-16">
                {steps.map((step, index) => (
                  <motion.div
                    key={step.number}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                      index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                    }`}
                  >
                    <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="flex items-center justify-center w-12 h-12 bg-primary text-white rounded-full font-bold text-lg">
                          {step.number}
                        </div>
                        <div className="text-primary">
                          {step.icon}
                        </div>
                      </div>
                      <h3 className="text-2xl font-bold mb-4">{step.title}</h3>
                      <p className="text-muted-foreground mb-6 text-lg leading-relaxed">
                        {step.description}
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-primary rounded-full"></div>
                          <span className="font-semibold text-primary">Our Way: {step.time}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-2 h-2 bg-muted rounded-full"></div>
                          <span className="text-muted-foreground">Traditional: {step.traditional}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className={`bg-gradient-to-br from-primary/10 to-secondary/10 p-8 rounded-2xl ${
                      index % 2 === 1 ? 'lg:col-start-1' : ''
                    }`}>
                      <div className="text-center">
                        <div className="text-primary mb-4">
                          {step.icon}
                        </div>
                        <div className="text-3xl font-bold text-primary mb-2">{step.time}</div>
                        <p className="text-muted-foreground">vs. {step.traditional}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Market Context Section */}
          <section className="py-24 bg-primary text-white">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Why Evaluate Only 1% of the Market?
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                      <div className="text-4xl font-bold mb-2">523</div>
                      <div className="text-lg opacity-90">Total PEOs in Market</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                      <div className="text-4xl font-bold mb-2">5</div>
                      <div className="text-lg opacity-90">Traditional Evaluation</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                      <div className="text-4xl font-bold mb-2">&lt;1%</div>
                      <div className="text-lg opacity-90">Market Coverage</div>
                    </div>
                  </div>
                  <p className="text-xl opacity-90 mb-8 leading-relaxed">
                    Most businesses end up with a PEO they found through Google searches or networking events. 
                    That's like buying a house after only seeing 5 options in your entire city. Our AI evaluates 
                    your requirements against all 523 PEOs to find the perfect fit, not just the best marketer.
                  </p>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="py-24 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <h2 className="text-3xl font-bold mb-6">Why Businesses Choose Our Method</h2>
                  <p className="text-muted-foreground mb-8 text-lg">
                    Join thousands of businesses who've eliminated the frustration of traditional PEO shopping.
                  </p>
                  <div className="space-y-4">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className="bg-background p-8 rounded-2xl border shadow-sm"
                >
                  <div className="text-center">
                    <Clock className="h-16 w-16 text-primary mx-auto mb-6" />
                    <h3 className="text-2xl font-bold mb-4">Ready to Save Time?</h3>
                    <p className="text-muted-foreground mb-6">
                      Stop wasting weeks on traditional PEO shopping. Get matched with qualified providers today.
                    </p>
                    <Button asChild size="lg" className="w-full">
                      <Link to="/signup">
                        Start Your Application
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default HowItWorks;